import type { ErrorInfo } from './orderErrorHandling';
/**
 * Error mappings for account operations (deposit/withdraw)
 */
export const accountErrorMapping: Record<string, ErrorInfo> = {
  // Deposit-specific errors
  InsufficientFunds: {
    title: 'Insufficient Funds',
    message: 'You do not have enough tokens to complete this deposit. Please check your wallet balance.',
    severity: 'error',
  },
  ContractInsufficientFunds: {
    title: 'Deposit Temporarily Unavailable',
    message:
      'We are unable to process your deposit right now. Please try again in a few minutes or contact us on Discord if the issue persists.',
    severity: 'warning',
  },
  AmountExceedsAvailableBalance: {
    title: 'Amount Exceeds Balance',
    message: 'The deposit amount exceeds your available balance. Please reduce the amount and try again.',
    severity: 'error',
  },

  // Withdrawal-specific errors
  WithdrawRateLimitExceeded: {
    title: 'Withdrawal Limit Reached',
    message:
      'You have reached the exchange-wide withdrawal limit for this period. Please try a smaller amount or try again later.',
    severity: 'warning',
  },
  InsufficentAmountToOpenAccount: {
    title: 'Deposit Too Small',
    message:
      'The amount is below the minimum required to open an account. Please check the minimum deposit amount and try again.',
    severity: 'error',
  },

  // Delegated-account authorization errors
  OwnableUnauthorizedAccount: {
    title: 'Not Authorized',
    message: 'This action can only be performed by the delegated account owner.',
    severity: 'error',
  },
  OnlyOwnerOrOperator: {
    title: 'Not Authorized',
    message: 'This action can only be performed by the delegated account owner or an authorized operator.',
    severity: 'error',
  },
  SelectorNotAllowed: {
    title: 'Not Authorized',
    message: 'This operator is not permitted to perform this action on the delegated account.',
    severity: 'error',
  },

  // General account errors
  AccountDoesNotExist: {
    title: 'Account Not Found',
    message: 'Your account was not found. Please ensure you are connected with the correct wallet.',
    severity: 'error',
  },
  AccountExists: {
    title: 'Account Already Exists',
    message: 'An account already exists for this address. You can proceed with your transaction.',
    severity: 'info',
  },
  NotWhitelisted: {
    title: 'Access Restricted',
    message: 'Your address is not currently approved for this action. Please join our Discord to request access.',
    severity: 'error',
  },
  AddressBlocked: {
    title: 'Address Blocked',
    message:
      'Your address has been blocked from performing this action. Please contact us on Discord if you believe this is an error.',
    severity: 'error',
  },
  IncompatibleCollateralToken: {
    title: 'Incompatible Token',
    message: 'The selected token is not compatible with this operation. Please use a supported token.',
    severity: 'error',
  },

  // Contract state errors
  ContractIsPaused: {
    title: 'Contract Paused',
    message: 'The contract is currently paused. Please wait for it to resume or contact us on Discord for updates.',
    severity: 'warning',
  },
  ContractDoesNotExist: {
    title: 'Contract Unavailable',
    message:
      'We could not connect to the required contract. Please refresh and try again, or contact us on Discord if the issue persists.',
    severity: 'error',
  },
};

export function getAccountErrorInfo(errorName: string, context?: string): ErrorInfo {
  const errorInfo = {
    ...(accountErrorMapping[errorName] || {
      title: 'Unknown Error',
      message: 'An unexpected error occurred. Please try again or contact support if the issue persists.',
      severity: 'error' as const,
    }),
  };

  // WithdrawRateLimitExceeded args are internal accounting values (CNS amounts, block heights)
  // that read as noise next to the user-facing message.
  if (context && errorName !== 'WithdrawRateLimitExceeded') {
    errorInfo.message = `${errorInfo.message} (${context})`;
  }

  return errorInfo;
}
